import type { EventHeader } from "@/lib/odds-screen/live/types";
import { playerMarketLabel } from "@/lib/market-label";
import { matchupOf } from "@/components/fantasy-props/model";
import { trimLine } from "@/components/odds-screen/live/labels";

export type SideLeg = { readonly side: string; readonly line: number; readonly market: string; readonly buyNo?: boolean };

type Bound = { readonly outcome: "OVER" | "UNDER"; readonly bound: number };

const UNITS: readonly (readonly [RegExp, string])[] = [
  [/home runs?/i, "HR"],
  [/shots on goal/i, "SOG"],
  [/three pointers?/i, "3PM"],
  [/field goals?/i, "FG"],
  [/yards?|yds/i, "yds"],
  [/receptions?/i, "rec"],
  [/points?|pts/i, "pts"],
  [/rebounds?/i, "reb"],
  [/assists?/i, "ast"],
  [/strikeouts?/i, "Ks"],
  [/touchdowns?|tds?\b/i, "TDs"],
  [/attempts?/i, "att"],
  [/completions?/i, "cmp"],
  [/hits?\b/i, "hits"],
  [/runs?\b/i, "runs"],
  [/goals?\b/i, "goals"],
  [/shots?\b/i, "shots"],
  [/saves?\b/i, "saves"],
];

function signed(value: number): string {
  return value > 0 ? `+${trimLine(value)}` : trimLine(value);
}

export type Teams = Pick<EventHeader, "home" | "away" | "homeShort" | "awayShort">;

function teamOf(event: Teams | undefined, side: "HOME" | "AWAY"): string {
  if (event === undefined) return side === "HOME" ? "Home" : "Away";
  return side === "HOME" ? event.homeShort ?? event.home : event.awayShort ?? event.away;
}

export function isMoneyline(market: string): boolean {
  return /moneyline/i.test(market);
}

export function sideLine(side: string, line: number): string {
  switch (side) {
    case "OVER": return `o${trimLine(line)}`;
    case "UNDER": return `u${trimLine(line)}`;
  }
  return signed(line);
}

export function sideLabel(leg: SideLeg, event: Teams | undefined): string {
  switch (leg.side) {
    case "OVER": return `Over ${trimLine(leg.line)}`;
    case "UNDER": return `Under ${trimLine(leg.line)}`;
    case "DRAW": return "Draw";
    case "YES": return "Yes";
    case "NO": return "No";
    case "HOME":
    case "AWAY": {
      const team = teamOf(event, leg.side);
      return isMoneyline(leg.market) ? `${team} ML` : `${team} ${signed(leg.line)}`;
    }
  }
  return leg.side;
}

export function betLabel(leg: SideLeg & { readonly buyNo: boolean }, event: EventHeader | undefined, entity: { readonly key: string; readonly name: string }): string {
  if (!leg.buyNo) return sideLabel(leg, event);
  switch (leg.side) {
    case "HOME":
    case "AWAY":
      return `No on ${sideLabel({ ...leg, side: leg.side === "HOME" ? "AWAY" : "HOME", line: -leg.line }, event)}`;
    case "UNDER":
      return entity.key === "" ? `No on Over ${trimLine(leg.line)}` : `No on ${entity.name} ${trimLine(Math.floor(leg.line) + 1)}+`;
    case "OVER":
      return `No on Under ${trimLine(leg.line)}`;
    case "NO":
      return "No";
  }
  return `No on ${sideLabel(leg, event)}`;
}

export function marketLabel(market: string, legs: readonly SideLeg[]): string {
  const shared = legs.length > 0 && legs.every((leg) => leg.market === legs[0].market) ? legs[0].market : market;
  return playerMarketLabel(shared);
}

export function statUnit(market: string): string {
  if (market.includes("+") || /longest/i.test(market)) return "";
  return UNITS.find(([pattern]) => pattern.test(market))?.[1] ?? "";
}

const PERIOD = /\b(1st|2nd|3rd|4th|first|second|half|quarter|period|inning)/i;

export function tiesPossible(league: string, market: string): boolean {
  if (league === "nfl" || league === "soccer") return true;
  if (league === "tennis" || league === "mma") return false;
  return PERIOD.test(market);
}

function boundOf(leg: SideLeg): Bound | null {
  switch (leg.side) {
    case "OVER": return { outcome: "OVER", bound: leg.line };
    case "UNDER": return { outcome: "UNDER", bound: leg.line };
    case "HOME": return { outcome: "OVER", bound: isMoneyline(leg.market) ? (leg.buyNo ? -0.5 : 0) : -leg.line };
    case "AWAY": return { outcome: "UNDER", bound: isMoneyline(leg.market) ? (leg.buyNo ? 0.5 : 0) : leg.line };
  }
  return null;
}

export function displayOrder(legs: readonly SideLeg[]): number[] {
  const order = legs.map((_, index) => index);
  if (legs.length !== 2) return order;
  const under = legs.findIndex((leg) => boundOf(leg)?.outcome === "UNDER");
  return under === 1 ? [1, 0] : order;
}

export type Span = { readonly over: number; readonly under: number; readonly margin: boolean };

export function spanOf(legs: readonly SideLeg[]): Span | null {
  const bounds = legs.map(boundOf);
  const over = bounds.find((bound) => bound?.outcome === "OVER");
  const under = bounds.find((bound) => bound?.outcome === "UNDER");
  if (legs.length !== 2 || !over || !under) return null;
  return { over: over.bound, under: under.bound, margin: legs.some((leg) => leg.side === "HOME" || leg.side === "AWAY") };
}

function rangeText(lo: number, hi: number): string {
  return lo === hi ? trimLine(lo) : `${trimLine(lo)}–${trimLine(hi)}`;
}

function marginRange(lo: number | null, hi: number | null, event: EventHeader | undefined, ties: boolean): string {
  const away = teamOf(event, "AWAY");
  const home = teamOf(event, "HOME");
  const parts: string[] = [];
  if (lo === null || lo < 0) {
    const least = hi === null || hi >= 0 ? 1 : -hi;
    if (lo !== null) parts.push(`${away} by ${rangeText(least, -lo)}`);
    else parts.push(least === 1 ? `${away} wins` : `${away} by ${trimLine(least)}+`);
  }
  if (ties && (lo === null || lo <= 0) && (hi === null || hi >= 0)) parts.push("tie");
  if (hi === null || hi > 0) {
    const least = lo === null || lo <= 0 ? 1 : lo;
    if (hi !== null) parts.push(`${home} by ${rangeText(least, hi)}`);
    else parts.push(least === 1 ? `${home} wins` : `${home} by ${trimLine(least)}+`);
  }
  const text = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} or ${parts[parts.length - 1]}` : parts[0] ?? "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function middleWindow(legs: readonly SideLeg[], market: string, event: EventHeader | undefined, league: string): string | null {
  const span = spanOf(legs);
  if (span === null || span.under <= span.over) return null;
  const lo = Math.floor(span.over) + 1;
  const hi = Math.ceil(span.under) - 1;
  if (hi < lo) return null;
  if (span.margin) return marginRange(lo, hi, event, tiesPossible(league, market));
  const unit = statUnit(market);
  return unit ? `${rangeText(lo, hi)} ${unit}` : rangeText(lo, hi);
}

export type Outcome = { readonly label: string; readonly legs: readonly number[]; readonly refunds: readonly number[] };

type Bucket = { lo: number | null; hi: number | null; key: string; legs: number[]; refunds: number[] };

function bucketsOf(legs: readonly SideLeg[], span: Span, ties: boolean): Bucket[] {
  const overIndex = legs.findIndex((leg) => boundOf(leg)?.outcome === "OVER");
  const underIndex = 1 - overIndex;
  const buckets: Bucket[] = [];
  const first = Math.floor(Math.min(span.over, span.under)) - 1;
  const last = Math.ceil(Math.max(span.over, span.under)) + 1;
  for (let result = first; result <= last; result += 1) {
    if (span.margin && !ties && result === 0) continue;
    const wins: number[] = [];
    const refunds: number[] = [];
    if (result > span.over) wins.push(overIndex);
    else if (result === span.over) refunds.push(overIndex);
    if (result < span.under) wins.push(underIndex);
    else if (result === span.under) refunds.push(underIndex);
    wins.sort((a, b) => a - b);
    refunds.sort((a, b) => a - b);
    const key = `${wins.join()}|${refunds.join()}`;
    const previous = buckets[buckets.length - 1];
    if (previous !== undefined && previous.key === key) previous.hi = result;
    else buckets.push({ lo: result, hi: result, key, legs: wins, refunds });
  }
  buckets[0].lo = null;
  buckets[buckets.length - 1].hi = null;
  return buckets;
}

function totalRange(lo: number | null, hi: number | null, tail: string): string {
  if (lo === null && hi !== null) return `${trimLine(hi)} or fewer${tail}`;
  if (hi === null && lo !== null) return `${trimLine(lo)}+${tail}`;
  return `${rangeText(lo ?? 0, hi ?? 0)}${tail}`;
}

export function outcomesOf(legs: readonly SideLeg[], market: string, event: EventHeader | undefined, league: string): Outcome[] {
  const span = spanOf(legs);
  if (span === null) return legs.map((leg, index) => ({ label: `${sideLabel(leg, event)} wins`, legs: [index], refunds: [] }));
  const unit = statUnit(market);
  const tail = unit ? ` ${unit}` : "";
  const ties = tiesPossible(league, market);
  return bucketsOf(legs, span, ties).map((bucket) => ({
    label: span.margin ? marginRange(bucket.lo, bucket.hi, event, ties) : totalRange(bucket.lo, bucket.hi, tail),
    legs: bucket.legs,
    refunds: bucket.refunds,
  }));
}

type TitleRow = { readonly marketType: "game" | "player"; readonly entity: { readonly key: string; readonly name: string } };

export function gameLabel(row: TitleRow, market: string): string {
  return row.entity.key === "" ? market : `${row.entity.name} ${market}`;
}

export function playTitle(row: TitleRow, event: EventHeader, market: string): string {
  if (row.marketType !== "game") return row.entity.name;
  return row.entity.key === "" ? `${matchupOf(event)} ${market}` : gameLabel(row, market);
}
